// Shared @mention-while-typing logic for GroupPostComposer.jsx and
// GroupPosts.jsx's comment input -- reuses the existing global search
// thunk's already-built "@" user-search mode (searchAPI.js/searchActions.js)
// rather than a new endpoint. See Technical_Docs/27_GROUP_POST_MENTIONS_DESIGN.md.
import { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { fetchGlobalSearchResults } from '../../../redux/searchBar/searchActions';

// An active "@partial" token ending exactly at the cursor -- must be
// preceded by start-of-string or whitespace, so "email@example.com" never
// triggers, and the partial itself must fit the real username charset.
const MENTION_TRIGGER_RE = /(?:^|\s)@([a-zA-Z0-9_]{1,32})$/;

export default function useMentionAutocomplete() {
  const dispatch = useDispatch();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mentionStart, setMentionStart] = useState(null); // index of '@' within the text, or null when inactive
  // The textarea must keep DOM focus while suggestions are open (the user
  // is still typing), so keyboard nav is tracked as plain state here rather
  // than via focus-shifting between <li> elements the way the global search
  // bar's SuggestionList does.
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const requestIdRef = useRef(0);

  const debouncedSearch = useRef(
    debounce(async (partial) => {
      const reqId = ++requestIdRef.current;
      setLoading(true);
      try {
        const result = await dispatch(fetchGlobalSearchResults(`@${partial}`)).unwrap();
        if (reqId === requestIdRef.current) {
          setSuggestions((result.users || []).slice(0, 6));
          setHighlightedIndex(0);
        }
      } catch {
        // The backend 400s on genuinely no matches -- that's "no
        // suggestions," not an error state worth surfacing here.
        if (reqId === requestIdRef.current) setSuggestions([]);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    }, 250)
  ).current;

  /** Call on every keystroke from the composer's onChange, passing the
   * full text and the cursor's current index. */
  const handleTextChange = useCallback((text, cursorIndex) => {
    const upToCursor = text.slice(0, cursorIndex);
    const match = upToCursor.match(MENTION_TRIGGER_RE);
    if (!match) {
      setMentionStart(null);
      setSuggestions([]);
      debouncedSearch.cancel();
      return;
    }
    const partial = match[1];
    const start = cursorIndex - partial.length - 1; // index of the '@' itself
    setMentionStart(start);
    debouncedSearch(partial);
  }, [debouncedSearch]);

  /** Given the current text/cursor and a selected user, returns the new
   * text with `@username ` inserted in place of the partial token, plus
   * the cursor position to move to afterward. */
  const applyMention = useCallback((text, cursorIndex, user) => {
    if (mentionStart === null) return { text, cursorIndex };
    const before = text.slice(0, mentionStart);
    const after = text.slice(cursorIndex);
    const inserted = `@${user.username} `;
    const newText = `${before}${inserted}${after}`;
    setMentionStart(null);
    setSuggestions([]);
    debouncedSearch.cancel();
    return { text: newText, cursorIndex: before.length + inserted.length };
  }, [mentionStart, debouncedSearch]);

  const closeSuggestions = useCallback(() => {
    setMentionStart(null);
    setSuggestions([]);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  /** Call from the textarea's onKeyDown while suggestions are open. Returns
   * the selected user on Enter/Tab (caller applies it via applyMention),
   * or null if the key was just navigation/dismissal (still handled, still
   * needs preventDefault from the caller). Returns undefined if the key
   * wasn't one this hook cares about, so the caller's own handler proceeds
   * normally (e.g. Enter to submit the post when no suggestions are open). */
  const handleKeyDown = useCallback((key) => {
    if (!suggestions.length) return undefined;
    if (key === 'ArrowDown') {
      setHighlightedIndex((i) => (i + 1) % suggestions.length);
      return null;
    }
    if (key === 'ArrowUp') {
      setHighlightedIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      return null;
    }
    if (key === 'Enter' || key === 'Tab') {
      return suggestions[highlightedIndex] || null;
    }
    if (key === 'Escape') {
      closeSuggestions();
      return null;
    }
    return undefined;
  }, [suggestions, highlightedIndex, closeSuggestions]);

  return {
    isActive: mentionStart !== null,
    suggestions,
    loading,
    highlightedIndex,
    handleTextChange,
    handleKeyDown,
    applyMention,
    closeSuggestions,
  };
}
